import { useCallback } from 'react';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';
import type { QuickFillItem } from '../../../shared/types';
import {
  buildFindReplaceValue,
  isFindReplaceValueMatch,
  resolveFindReplaceRowIndices,
  type FindReplaceOptions,
  type SearchMatch,
} from '../../../shared/lib/utils/findReplaceUtils';
import type { QuickFillMutationOperations } from './useQuickFillMutationOperations';

// Declared input contract: only these fields of the mutation facade are used.
// Handlers read the latest table data via useQuickFillStore.getState() on
// purpose — subscribing here would re-create these callbacks on every
// keystroke and defeat the memoized table rows.
export type QuickFillFindReplaceContext = Pick<QuickFillMutationOperations,
  | 'selectedRows'
  | 'canEditCell'
  | 'applyFieldValueToRows'
  | 'captureSessionUndoBeforeMutation'
  | 'setQuickFillData'
  | 'snackbar'
>;

export function useQuickFillFindReplaceOperations(context: QuickFillFindReplaceContext) {
  const {
    selectedRows, canEditCell, applyFieldValueToRows, captureSessionUndoBeforeMutation,
    setQuickFillData, snackbar,
  } = context;

  const handleFind = useCallback((
    searchTerm: string, options: FindReplaceOptions
  ): SearchMatch[] => {
    const currentState = useQuickFillStore.getState();
    const data = Array.isArray(currentState.quickFillData) ? currentState.quickFillData : [];
    const properties = Array.isArray(currentState.properties) ? currentState.properties : [];
    const columns = options.scope === 'column' && options.scopeColumn
      ? [options.scopeColumn]
      : properties.filter((property) => property?.checked)
        .map((property) => property.label).filter((label) => label !== 'Name');
    const scopedRows = resolveFindReplaceRowIndices(data.length, options, selectedRows);
    const rows = scopedRows ?? data.map((_item, index) => index);
    const matches: SearchMatch[] = [];
    rows.forEach((rowIndex) => {
      const item = data[rowIndex];
      if (!item) return;
      columns.forEach((column) => {
        const value = String(item[column as keyof QuickFillItem] || '');
        if (isFindReplaceValueMatch(value, searchTerm, options)) {
          matches.push({ row: rowIndex, column, value });
        }
      });
    });
    return matches;
  }, [selectedRows]);

  const handleReplace = useCallback(async (
    match: SearchMatch,
    replaceValue: string,
    searchTerm = match.value,
    options: FindReplaceOptions = {
      caseSensitive: false,
      wholeCell: true,
      scope: 'all',
    }
  ) => {
    const currentState = useQuickFillStore.getState();
    const data = Array.isArray(currentState.quickFillData) ? currentState.quickFillData : [];
    const row = data[match.row];
    if (!canEditCell(row, match.column)) return;
    const currentValue = String(row?.[match.column as keyof QuickFillItem] || '');
    const newValue = buildFindReplaceValue(currentValue, searchTerm, replaceValue, options);
    if (newValue === currentValue) return;
    const applied = applyFieldValueToRows(
      data, [match.row], match.column as keyof QuickFillItem, newValue
    );
    if (applied.touchedRowIndices.length === 0) return;
    captureSessionUndoBeforeMutation('Find/replace');
    setQuickFillData(applied.updatedData);
    // Staged only: the Revit document is written by the Apply/Continue submit path.
  }, [
    applyFieldValueToRows, canEditCell,
    captureSessionUndoBeforeMutation, setQuickFillData,
  ]);

  const handleReplaceAll = useCallback(async (
    searchTerm: string, replaceValue: string, options: FindReplaceOptions
  ): Promise<number> => {
    const matches = handleFind(searchTerm, options);
    if (matches.length === 0) return 0;
    const currentState = useQuickFillStore.getState();
    const data = Array.isArray(currentState.quickFillData) ? currentState.quickFillData : [];
    const updatedData = [...data];
    let changedCells = 0;

    matches.forEach((match) => {
      const item = updatedData[match.row];
      if (!item || !canEditCell(item, match.column)) return;
      const currentValue = String(item[match.column as keyof QuickFillItem] || '');
      const newValue = buildFindReplaceValue(currentValue, searchTerm, replaceValue, options);
      if (newValue === currentValue) return;
      const applied = applyFieldValueToRows(
        updatedData, [match.row], match.column as keyof QuickFillItem, newValue
      );
      if (applied.touchedRowIndices.length === 0) return;
      updatedData.splice(0, updatedData.length, ...applied.updatedData);
      changedCells += applied.touchedRowIndices.length;
    });

    if (changedCells === 0) {
      snackbar.showWarning('No matching editable cells changed');
      return 0;
    }
    captureSessionUndoBeforeMutation('Find/replace all');
    setQuickFillData(updatedData);
    // Staged only: the Revit document is written by the Apply/Continue submit path.
    snackbar.showSuccess(`Replaced ${changedCells} cell${changedCells === 1 ? '' : 's'}`);
    return changedCells;
  }, [
    applyFieldValueToRows, canEditCell, captureSessionUndoBeforeMutation,
    handleFind, setQuickFillData, snackbar,
  ]);

  return { handleFind, handleReplace, handleReplaceAll };
}
